import React, { useEffect, useState } from 'react';
import { Table, Tag, Space, Button, Input, Select, Card, Row, Col, Typography, Tooltip, Popconfirm, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Product, ProductFilter } from '../types/products';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import ProductConfigModal from '../components/products/ProductConfigModal';
import { Category } from '../types/categories';
import { useSnackbar } from 'notistack';

const { Search } = Input;
const { Title, Text } = Typography;

const Products: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filter, setFilter] = useState<ProductFilter>({
        page: 1,
        limit: 10
    });
    const [search, setSearch] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [configModalVisible, setConfigModalVisible] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Fetch categories for filter
    const fetchCategories = async () => {
        try {
            const res = await categoryService.getCategories();
            setCategories(res.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilter(prev => ({
                ...prev,
                search: search || undefined,
                page: 1
            }));
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [search]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const result = await productService.getProducts(filter);
            setProducts(result.products);
            setTotal(result.total);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshProducts = async () => {
        try {
            setLoading(true);
            // Clear filters when refetching
            const clearFilter: ProductFilter = {
                page: 1,
                limit: 10
            };
            setFilter(clearFilter);
            setSearch('');
            setStatusFilter('');
            setCategoryFilter('');
            setCurrentPage(1);

            const result = await productService.getProducts(clearFilter);
            setProducts(result.products);
            setTotal(result.total);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [filter]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const columns = [
        {
            title: 'Tên sản phẩm',
            dataIndex: 'name',
            key: 'name',
            align: 'center' as const,
            render: (text: string, record: Product) => (
                <Tooltip title={`Tên sản phẩm: ${text}`}>
                    <a>{text}</a>
                </Tooltip>
            ),
        },
        {
            title: 'Danh mục',
            dataIndex: ['category', 'name'],
            key: 'category',
            align: 'center' as const,
            render: (categoryName: string) => (
                <Tooltip title={`Danh mục: ${categoryName}`}>
                    <span>{categoryName}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Link',
            key: 'link',
            align: 'center' as const,
            render: (_: any, record: Product) => (
                record.isVisible ? (
                    <Tooltip title={`Xem sản phẩm: ${record.name}`}>
                        <a
                            href={`https://desilk.com.vn/product/${record.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            Link
                        </a>
                    </Tooltip>
                ) : (
                    <Tooltip title="Sản phẩm đang ẩn">
                        <span className="text-gray-400 cursor-not-allowed">Link</span>
                    </Tooltip>
                )
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            render: (status: string) => {
                let color = 'default';
                let text = '';
                switch (status) {
                    case 'available':
                        color = 'green';
                        text = 'Còn hàng';
                        break;
                    case 'out_of_stock':
                        color = 'red';
                        text = 'Hết hàng';
                        break;
                    case 'discontinued':
                        color = 'orange';
                        text = 'Ngừng bán';
                        break;
                    default:
                        text = status;
                }
                return (
                    <Tooltip title={`Trạng thái: ${text}`}>
                        <Tag color={color}>{text}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Giá',
            key: 'price',
            align: 'center' as const,
            render: (record: Product) => {
                const priceText = record.price.sale
                    ? `Giá gốc: ${record.price.regular.toLocaleString('vi-VN')}đ - Giá bán: ${record.price.sale.toLocaleString('vi-VN')}đ`
                    : `Giá: ${record.price.regular.toLocaleString('vi-VN')}đ`;

                return (
                    <Tooltip title={priceText}>
                        {record.price.sale ? (
                            <span>
                                <span style={{ textDecoration: 'line-through', color: '#888', marginRight: 8 }}>
                                    {record.price.regular.toLocaleString('vi-VN')}đ
                                </span>
                                <span style={{ color: '#d4380d', fontWeight: 600 }}>
                                    {record.price.sale.toLocaleString('vi-VN')}đ
                                </span>
                            </span>
                        ) : (
                            <span>{record.price.regular.toLocaleString('vi-VN')}đ</span>
                        )}
                    </Tooltip>
                );
            },
        },
        {
            title: 'Hiển thị',
            dataIndex: 'isVisible',
            key: 'isVisible',
            align: 'center' as const,
            render: (isVisible: boolean, record: Product) => (
                <Tooltip title={isVisible ? 'Đang hiển thị' : 'Đang ẩn'}>
                    <Switch
                        checked={isVisible}
                        onChange={() => handleToggleVisibility(record._id, isVisible)}
                        checkedChildren="Hiện"
                        unCheckedChildren="Ẩn"
                    />
                </Tooltip>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            align: 'center' as const,
            render: (_: any, record: Product) => (
                <Space>
                    <Tooltip title="Chỉnh sửa sản phẩm">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/products/${record._id}`)}
                            type="text"
                        />
                    </Tooltip>
                    <Tooltip title="Xóa sản phẩm">
                        <Popconfirm
                            title="Xóa sản phẩm"
                            description="Bạn có chắc chắn muốn xóa sản phẩm này?"
                            onConfirm={() => handleDelete(record._id)}
                            okText="Xóa"
                            cancelText="Hủy"
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                type="text"
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const handleTableChange = (pagination: any) => {
        setFilter(prev => ({
            ...prev,
            page: pagination.current,
            limit: pagination.pageSize
        }));
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleDelete = async (id: string) => {
        try {
            setLoading(true);
            await productService.deleteProduct(id);
            enqueueSnackbar('Xóa sản phẩm thành công', { variant: 'success' });
            fetchProducts(); // Refresh lại danh sách
        } catch (error) {
            enqueueSnackbar('Xóa sản phẩm thất bại', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleVisibility = async (id: string, currentIsVisible: boolean) => {
        try {
            await productService.updateProduct(id, { isVisible: !currentIsVisible });
            enqueueSnackbar(`Đã ${!currentIsVisible ? 'hiển thị' : 'ẩn'} sản phẩm`, { variant: 'success' });
            fetchProducts(); // Refresh lại danh sách
        } catch (error) {
            enqueueSnackbar('Cập nhật trạng thái hiển thị thất bại', { variant: 'error' });
        }
    };

    const handleConfigSave = () => {
        setConfigModalVisible(false);
        enqueueSnackbar('Đã lưu cấu hình', { variant: 'success' });
    };

    return (
        <div className="p-4">
            {/* Header row với bộ lọc */}
            <Card className="mb-4">
                <Row justify="space-between" align="middle" gutter={16}>
                    <Col>
                        <Title level={2} className="m-0">
                            Quản lý sản phẩm
                        </Title>
                    </Col>
                    <Col>
                        <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                            Cấu hình
                        </Button>
                    </Col>
                </Row>
                <Row justify="space-between" align="middle" gutter={16} className="mt-4">
                    <Col>
                        <Space>
                            <Input
                                placeholder="Tìm kiếm sản phẩm..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                prefix={<SearchOutlined />}
                                allowClear
                                style={{ width: 300 }}
                            />
                            <Select
                                placeholder="Danh mục"
                                style={{ width: 200 }}
                                value={categoryFilter || undefined}
                                onChange={(value) => {
                                    setCategoryFilter(value);
                                    setFilter(prev => ({ ...prev, category: value }));
                                }}
                                allowClear
                            >
                                {categories.map(category => (
                                    <Select.Option key={category._id} value={category._id}>
                                        {category.name}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Select
                                placeholder="Trạng thái"
                                style={{ width: 200 }}
                                value={statusFilter || undefined}
                                onChange={(value) => {
                                    setStatusFilter(value);
                                    setFilter(prev => ({ ...prev, status: value }));
                                }}
                                allowClear
                            >
                                <Select.Option value="available">Còn hàng</Select.Option>
                                <Select.Option value="out_of_stock">Hết hàng</Select.Option>
                                <Select.Option value="discontinued">Ngừng bán</Select.Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={refreshProducts}
                                loading={loading}
                                shape="circle"
                                title="Làm mới"
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/admin/products/new')}
                            >
                                Thêm sản phẩm
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Bảng sản phẩm */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={products}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showTotal: (total) => `Tổng số ${total} sản phẩm`
                    }}
                    onChange={handleTableChange}
                    bordered
                />
            </Card>

            <ProductConfigModal
                visible={configModalVisible}
                onCancel={() => setConfigModalVisible(false)}
                onSave={handleConfigSave}
            />
        </div>
    );
};

export default Products;
